/**
 * Required two-factor setup for a user without an authenticator app: scan
 * the QR code (or type the key), then confirm with the first code.
 */

import { useState } from 'react';
import { useAuth } from '@shared/auth/AuthContext';
import { totpErrorMessage } from '@shared/auth/totpErrorMessage';
import type { TotpEnrollment } from '@shared/auth/types';
import {
  AUTH_BODY_CLASS,
  AUTH_ERROR_CLASS,
  AUTH_HEADING_CLASS,
  AUTH_LINK_BUTTON_CLASS,
  AUTH_SUBMIT_CLASS,
  AUTH_SUBMIT_STYLE,
} from './authFormStyles';
import { TotpCodeForm } from './TotpCodeForm';

const QR_CODE_SIZE_PX = 180;

/** "JBSWY3DPEHPK3PXP" → "JBSW Y3DP EHPK 3PXP", easier to type. */
function groupSecret(secret: string): string {
  return secret.match(/.{1,4}/g)?.join(' ') ?? secret;
}

export function TotpEnrollStep() {
  const { startTotpEnrollment, confirmTotpEnrollment, logout } = useAuth();
  const [enrollment, setEnrollment] = useState<TotpEnrollment | null>(null);
  const [isStarting, setIsStarting] = useState(false);
  const [error, setError] = useState('');

  const handleStart = async () => {
    setError('');
    setIsStarting(true);
    try {
      setEnrollment(await startTotpEnrollment());
    } catch (err: unknown) {
      setError(totpErrorMessage(err));
    } finally {
      setIsStarting(false);
    }
  };

  return (
    <div className="w-full">
      <h2 className={AUTH_HEADING_CLASS}>Set up two-factor authentication</h2>

      {enrollment ? (
        <>
          <p className={AUTH_BODY_CLASS}>1. Scan this QR code with your authenticator app.</p>
          <div className="mb-3 flex justify-center">
            <img
              src={enrollment.qrCodeSrc}
              alt="QR code to add Luminaria to your authenticator app"
              width={QR_CODE_SIZE_PX}
              height={QR_CODE_SIZE_PX}
              className="rounded-lg bg-white p-2"
            />
          </div>
          <details className="mb-4 text-sm text-text-muted">
            <summary className="cursor-pointer">Can't scan it? Enter this key instead</summary>
            <code className="mt-2 block select-all break-all rounded bg-surface p-2 font-mono text-text">
              {groupSecret(enrollment.secret)}
            </code>
          </details>
          <p className={AUTH_BODY_CLASS}>2. Enter the 6-digit code the app shows.</p>
          <TotpCodeForm
            submitLabel="Verify and continue"
            onSubmit={(code) => confirmTotpEnrollment(enrollment.factorId, code)}
          />
        </>
      ) : (
        <>
          <p className={AUTH_BODY_CLASS}>
            Luminaria requires an authenticator app (such as 1Password, Google Authenticator or
            Authy) in addition to your password.
          </p>
          {error && (
            <div role="alert" className={AUTH_ERROR_CLASS}>
              {error}
            </div>
          )}
          <button
            type="button"
            onClick={() => void handleStart()}
            disabled={isStarting}
            className={AUTH_SUBMIT_CLASS}
            style={AUTH_SUBMIT_STYLE}
          >
            {isStarting ? 'Preparing...' : 'Set up authenticator app'}
          </button>
        </>
      )}

      <div className="mt-4">
        <button type="button" onClick={() => void logout()} className={AUTH_LINK_BUTTON_CLASS}>
          Sign out
        </button>
      </div>
    </div>
  );
}
